import React, { useEffect, useState, useContext } from 'react';
import api, { getUserFacingErrorMessage } from '../utils/api';
import { useLocation, useNavigate } from 'react-router-dom';
import NomineeRequiredBanner from './NomineeRequiredBanner';

export default function WillInformation({ nomineeGate = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [current, setCurrent] = useState(null);
  const [info, setInfo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [listRes, infoRes] = await Promise.all([
      api.get('/will/list'),
      api.get('/will/info')
    ]);
    setCurrent(listRes.data.currentFile || null);
    setInfo(infoRes.data || null);
  };

  useEffect(() => { load(); }, []);

  const [form, setForm] = useState({
    location: '',
    dateSigned: '',
    witness1Name: '',
    witness1Email: '',
    witness2Name: '',
    witness2Email: '',
    inNationalRegister: false,
  });

  useEffect(() => {
    if (info) {
      setForm({
        location: info.location || '',
        dateSigned: info.dateSigned || '',
        witness1Name: info.witness1Name || '',
        witness1Email: info.witness1Email || '',
        witness2Name: info.witness2Name || '',
        witness2Email: info.witness2Email || '',
        inNationalRegister: !!info.inNationalRegister,
      });
    } else {
      setForm({
        location: '', dateSigned: '', witness1Name: '', witness1Email: '', witness2Name: '', witness2Email: '', inNationalRegister: false,
      });
    }
  }, [info]);

  const save = async () => {
    if (nomineeGate) {
      navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      return;
    }
    setBusy(true);
    try {
      await api.post('/will/info', form);
      await load();
      setEditing(false);
      alert('Information saved.');
    } catch (e) {
      console.error(e);
      const code = e?.response?.data?.code;
      if (e?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(e, {
          fallback: 'Add at least one nominee before you can update will information.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      } else {
        alert(getUserFacingErrorMessage(e, {
          fallback: 'We could not save your will information. Please try again.',
          includeSupportForUnknown: false,
        }));
      }
    } finally {
      setBusy(false);
    }
  };

  const removeInfo = async () => {
    if (nomineeGate) {
      navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      return;
    }
    if (!window.confirm('Remove will information?')) return;
    setBusy(true);
    try {
      await api.delete('/will/info');
      await load();
      setEditing(false);
      alert('Information removed.');
    } catch (e) {
      console.error(e);
      const code = e?.response?.data?.code;
      if (e?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(e, {
          fallback: 'Add at least one nominee before you can update will information.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      } else {
        alert(getUserFacingErrorMessage(e, {
          fallback: 'We could not remove your will information. Please try again.',
          includeSupportForUnknown: false,
        }));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {nomineeGate && <NomineeRequiredBanner returnTo={location.pathname} />}
      <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Stored Will</div>
        {!current && <div style={{ color: '#666' }}>No will uploaded yet.</div>}
        {current && (
          <div>
            <div style={{ fontSize: 14 }}>Uploaded on {new Date(current.date).toLocaleString()}</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const r = await api.get('/will/current');
                    const url = r?.data?.url;
                    if (!url) throw new Error('No URL returned');
                    const tab = window.open(url, '_blank');
                    if (tab) {
                      try { tab.opener = null; } catch {}
                    }
                  } catch (e) {
                    console.error(e);
                    alert('Could not open preview. Please try again.');
                  }
                }}
                style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--mist-200)', color: 'var(--dusk-800)', border: 'none', cursor: 'pointer' }}
              >
                Preview
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const r = await api.get('/will/current/download');
                    const url = r?.data?.url;
                    if (!url) throw new Error('No URL returned');
                    window.location.assign(url);
                  } catch (e) {
                    console.error(e);
                    alert('Could not download file. Please try again.');
                  }
                }}
                style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--mist-200)', color: 'var(--dusk-800)', border: 'none', cursor: 'pointer' }}
              >
                Download
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700 }}>Original Will Details</div>
          {!editing ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => (nomineeGate ? navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } }) : setEditing(true))}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--mist-300)', background: '#fff' }}
                aria-disabled={nomineeGate}
                title={nomineeGate ? 'Add a nominee first' : 'Edit'}
              >
                Edit
              </button>
              {info && (
                <button
                  type="button"
                  onClick={removeInfo}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--mist-300)', background: '#fff' }}
                  disabled={nomineeGate}
                  title={nomineeGate ? 'Add a nominee first' : 'Remove'}
                >
                  Remove
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={save} disabled={busy} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'var(--sage-600)', color: '#fff', fontWeight: 600 }}>{busy ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={() => { setEditing(false); setInfo(info); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--mist-300)', background: '#fff' }}>Cancel</button>
            </div>
          )}
        </div>

        {!editing ? (
          <div style={{ marginTop: 12, color: '#333' }}>
            {!info && <div style={{ color: '#666' }}>No information provided yet.</div>}
            {info && (
              <div style={{ display: 'grid', gap: 8 }}>
                <div><b>Location</b>: {info.location || '-'}</div>
                <div><b>Date signed</b>: {info.dateSigned || '-'}</div>
                <div><b>Witness 1</b>: {info.witness1Name || '-'} {info.witness1Email ? `(${info.witness1Email})` : ''}</div>
                <div><b>Witness 2</b>: {info.witness2Name || '-'} {info.witness2Email ? `(${info.witness2Email})` : ''}</div>
                <div><b>Registered with National Will Register</b>: {info.inNationalRegister ? 'Yes' : 'No'}</div>
                {info.updatedAt && <div style={{ fontSize: 13, color: '#666' }}>Last updated {new Date(info.updatedAt).toLocaleString()}</div>}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            <label>Location of original copy<br/>
              <input value={form.location} onChange={e=>setForm(f=>({ ...f, location: e.target.value }))} aria-label="Original will location" />
            </label>
            <label>Date signed<br/>
              <input type="date" value={form.dateSigned} onChange={e=>setForm(f=>({ ...f, dateSigned: e.target.value }))} aria-label="Date signed" />
            </label>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
              <label>Witness 1 name<br/>
                <input value={form.witness1Name} onChange={e=>setForm(f=>({ ...f, witness1Name: e.target.value }))} aria-label="Witness 1 name" />
              </label>
              <label>Witness 1 email<br/>
                <input type="email" value={form.witness1Email} onChange={e=>setForm(f=>({ ...f, witness1Email: e.target.value }))} aria-label="Witness 1 email" />
              </label>
            </div>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
              <label>Witness 2 name<br/>
                <input value={form.witness2Name} onChange={e=>setForm(f=>({ ...f, witness2Name: e.target.value }))} aria-label="Witness 2 name" />
              </label>
              <label>Witness 2 email<br/>
                <input type="email" value={form.witness2Email} onChange={e=>setForm(f=>({ ...f, witness2Email: e.target.value }))} aria-label="Witness 2 email" />
              </label>
            </div>
            <label>
              <input type="checkbox" checked={!!form.inNationalRegister} onChange={e=>setForm(f=>({ ...f, inNationalRegister: e.target.checked }))} aria-label="In National Will Register" />
              {' '}Is this will stored in the National Will Register?
            </label>
          </div>
        )}
      </div>
    </div>
  );
}


